/**
 * Realtime (SSE) connection recovery rules (#574, follow-up to #458).
 *
 * `EventSource` only reconnects by itself for the *transport* kind of failure —
 * the stream drops while it is open, `readyState` goes back to `CONNECTING`, and
 * the browser retries a few seconds later. For the *protocol* kind — the
 * response came back with a non-2xx status, or with a content type that is not
 * `text/event-stream` — the spec says the user agent "fails the connection":
 * `readyState` becomes `CLOSED`, `error` fires once, and **nothing ever retries**.
 *
 * That second case is what pins the "Reconnecting live updates" banner. The app
 * builds its `EventSource` inside one effect keyed on the signed-in user, so a
 * fatal close left the session with no live updates at all and no way back
 * short of a reload — while the 5s grace timer, which only `onopen` /
 * `connected` can clear, elapsed and stayed elapsed. #458 fixed the *flashing*
 * banner; a *pinned* one is this, because a connection that keeps blipping and
 * recovering on the browser's own ~3s retry never outlasts the grace window at
 * all.
 *
 * So the client owns the retry for fatal closes, with a capped exponential
 * backoff, and the grace window is measured from when the outage STARTED rather
 * than from the last error — otherwise each app-driven reopen would restart the
 * 5s clock and the banner could be deferred indefinitely by our own retries.
 *
 * None of this claims to know *why* the endpoint failed. If the deployment's
 * edge really is refusing or mangling `/api/v1/household/events`, the banner
 * still appears after the grace window and stays until something opens — which
 * is the banner doing its job. What changes is that the session recovers on its
 * own the moment the endpoint comes back.
 *
 * The rules live here rather than inline in App.tsx so they can be tested
 * without a DOM or a live server; App.tsx keeps the timers and the actual
 * `EventSource`.
 */

/** `EventSource.readyState` values, named so this module needs no DOM. */
export const eventSourceConnecting = 0;
export const eventSourceOpen = 1;
export const eventSourceClosed = 2;

/**
 * How long a realtime drop must persist before the "Reconnecting" banner shows.
 * Transient EventSource errors reconnect well within this, so they stay silent.
 */
export const realtimeReconnectGraceMs = 5000;

/** First app-driven reopen delay after a fatal close, then doubling. */
export const realtimeReopenBaseMs = 1000;

/**
 * Ceiling for the backoff. A dead endpoint is retried forever — one request
 * every 30s is negligible, and giving up permanently is the bug being fixed —
 * but it must never become a hot loop against an edge that is refusing us.
 */
export const realtimeReopenMaxMs = 30_000;

/**
 * Delay before the app reopens the stream itself, after `failedAttempts`
 * consecutive fatal closes (0 = the first one). Exponential, capped.
 */
export function realtimeReopenDelayMs(failedAttempts: number): number {
  const attempts = Number.isFinite(failedAttempts) && failedAttempts > 0 ? Math.floor(failedAttempts) : 0;
  // Cap the exponent before the shift so a long-lived tab cannot overflow it
  // into Infinity (or, with `<<`, wrap around to a negative delay).
  const doublings = Math.min(attempts, 30);
  return Math.min(realtimeReopenBaseMs * 2 ** doublings, realtimeReopenMaxMs);
}

export type RealtimeErrorPlan = {
  /**
   * Arm the grace window that eventually raises the banner. Always true: every
   * error means the stream is not currently carrying updates, and only an
   * actual open (`onopen` / the server's `connected` event) may clear it.
   */
  armGrace: true;
  /**
   * Milliseconds until the app must build a fresh `EventSource`, or `null` when
   * the browser is going to reconnect on its own and a second stream would just
   * be a duplicate subscription.
   */
  reopenInMs: number | null;
};

/**
 * What to do about an `EventSource` `error` event, given the source's
 * `readyState` at the moment it fired and how many fatal closes have already
 * gone unanswered since the last successful open.
 */
export function planRealtimeError(input: { readyState: number; failedAttempts: number }): RealtimeErrorPlan {
  const browserWillRetry = input.readyState !== eventSourceClosed;
  return {
    armGrace: true,
    reopenInMs: browserWillRetry ? null : realtimeReopenDelayMs(input.failedAttempts)
  };
}

/**
 * Milliseconds left on the grace window for an outage that began at
 * `outageSinceMs`, clamped to zero once it has already elapsed.
 *
 * Measured from the start of the outage on purpose: the app tears its
 * `EventSource` down and rebuilds it on every reopen, and re-arming a fresh 5s
 * timer each time would let a run of fast retries hold the banner off forever.
 */
export function remainingReconnectGraceMs(outageSinceMs: number, nowMs: number): number {
  return Math.max(0, outageSinceMs + realtimeReconnectGraceMs - nowMs);
}
